import { useNavigate } from "react-router-dom";
import FormLayout from "src/layouts/FormLayout";
import {
  useRedirectToPage,
  useRoleForBorrower,
  useSectionProgress,
} from "src/hooks";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  fromApplicationSummary,
  type LoanOverviewFormData,
} from "src/api/adapters";
import type { DtoApplicationSummaryResp } from "@porchpass/api-client";
import { useAuth, useUser } from "src/context";
import { InfoIconButton } from "src/components/InfoIconButton";
import LoanOverviewItem from "./LoanOverviewItem";
import CostBreakdownModal from "./CostBreakdownModal";
import { useState, type ButtonHTMLAttributes } from "react";
import MonthlyCalculationModal from "./MonthlyCalculationModal/MonthlyCalculationModal";
import TotalLoanCalculationModal from "./TotalLoanCalculationModal";
import GenericEditLoanOverviewModal from "./GenericEditLoanDetailModal";
import { safeNumber, safeRateToPercent, toFormattedInteger } from "src/utils";
import { Col } from "src/components/layout";
import {
  Alert,
  Button,
  DollarSquareIcon,
  Edit02Icon,
  FileDollarIcon,
  FormTable,
  FormTableBody,
  Spinner,
} from "@porchpass/ui";

type ModalState = "COST_BREAKDOWN" | "MONTHLY" | "YEARLY";

function DollarCircleIcon() {
  return (
    <div className="rounded-full bg-[#f3f4f7] p-2 text-[#687083]">
      <DollarSquareIcon size={25} />
    </div>
  );
}

function EditIconButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-8 w-8 rounded-lg bg-[#f3f4f7] text-[#687083]"
      {...props}
    >
      <Edit02Icon size={12} />
    </Button>
  );
}

export type LoanOverviewDetail = {
  label: string;
  id: keyof LoanOverviewFormData;
  amount: string | null;
  type: "dollar" | "percent";
};

export default () => {
  const api = useWebClientApi();
  const { user: firebaseUser } = useAuth();
  const role = useRoleForBorrower();
  const sectionProgress = useSectionProgress();
  const coborrowerNavigate = useRedirectToPage("/ethnicity");
  if (role === "COBORROWER") {
    coborrowerNavigate();
  }
  const navigate = useNavigate();
  const { userData } = useUser();
  const [modalState, setModalState] = useState<ModalState>();
  const [needsHomeInfo, setNeedsHomeInfo] = useState<boolean>(false);
  const [editModalState, setEditModalState] = useState<LoanOverviewDetail>();
  const { formData, reloadFormData, loading } =
    useFormLoad<LoanOverviewFormData>(
      {},
      () => api.getApplicationSummary(),
      (data) => {
        const summary = data as DtoApplicationSummaryResp;
        if (summary.home_price === undefined) {
          setNeedsHomeInfo(true);
        }
        return fromApplicationSummary(summary);
      },
      { enabled: !!firebaseUser },
    );

  const closeHandler = () => {
    setModalState(undefined);
    setEditModalState(undefined);
  };

  const confirmHandler = () => {
    setEditModalState(undefined);
    reloadFormData();
  };

  const loanOverviewDetails: LoanOverviewDetail[] = [
    {
      label: "Home Sale Price",
      id: "home_price",
      amount:
        formData.home_price !== null
          ? safeNumber(formData.home_price).toString()
          : null,
      type: "dollar",
    },
    {
      label: "Home Upgrades",
      id: "home_upgrades_cost",
      amount:
        formData.home_upgrades_cost !== null
          ? safeNumber(formData.home_upgrades_cost).toString()
          : null,
      type: "dollar",
    },
    {
      label: "Site Improvement Cost",
      id: "site_improvement_amount",
      amount:
        formData.site_improvement_amount !== null
          ? safeNumber(formData.site_improvement_amount).toString()
          : null,
      type: "dollar",
    },
    {
      label: "Land Purchase Price/Payoff",
      id: "land_purchase_or_payoff_amount",
      amount:
        formData.land_purchase_or_payoff_amount !== null
          ? safeNumber(formData.land_purchase_or_payoff_amount).toString()
          : null,
      type: "dollar",
    },
    {
      label: "Interest Rate",
      id: "loan_interest_rate",
      amount:
        formData.loan_interest_rate !== null
          ? safeRateToPercent(formData.loan_interest_rate)
          : null,
      type: "percent",
    },
    {
      label: "Home Insurance",
      id: "home_insurance_rate",
      amount:
        formData.home_insurance_rate !== null
          ? safeRateToPercent(formData.home_insurance_rate)
          : null,
      type: "percent",
    },
    {
      label: "Property Tax",
      id: "property_tax_rate",
      amount:
        formData.property_tax_rate !== null
          ? safeRateToPercent(formData.property_tax_rate)
          : null,
      type: "percent",
    },
    {
      label: "Down Payment",
      id: "down_payment_amount",
      amount:
        formData.down_payment_amount !== null
          ? safeNumber(formData.down_payment_amount).toString()
          : null,
      type: "dollar",
    },
  ];

  const getFooterTitle = () => {
    if (needsHomeInfo) {
      return "OK";
    }

    return sectionProgress["loan_overview"] && sectionProgress["consent"]
      ? "Save"
      : "Next";
  };

  const filteredLoanOverviewDetails: (LoanOverviewDetail & {
    amount: string;
  })[] = loanOverviewDetails
    .filter((item) => {
      if (
        item.id === "land_purchase_or_payoff_amount" &&
        !formData.land_purchase_or_payoff_amount
      ) {
        return false;
      }

      if (item.amount === null) {
        return false;
      }

      return true;
    })
    .map((item) => ({
      ...item,
      amount: item.amount as string,
    }));

  const nextPagePath =
    sectionProgress["loan_overview"] && sectionProgress["consent"]
      ? "../../progress"
      : "/ethnicity";

  const navigateToNextPage = useRedirectToPage(nextPagePath);

  const handleFooterClick = () => {
    if (needsHomeInfo) {
      return navigate(
        `/${userData?.dealer_id}/application/${userData?.application_id}/progress`,
      );
    }

    navigateToNextPage();
  };

  const renderContent = () => {
    if (needsHomeInfo) {
      return (
        <Col>
          <Alert variant="error">
            Please complete the “Home and Land” section of your application to
            see your loan overview.
          </Alert>
        </Col>
      );
    }

    if (loading) {
      return (
        <Col>
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <Spinner variant="blue" size="l" />
            <p>Calculating...</p>
          </div>
        </Col>
      );
    }

    return (
      <Col>
        <FormTable
          title="Loan Overview"
          headerIcon={
            <FileDollarIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
        >
          <FormTableBody className="space-y-12">
            <div className="flex w-full flex-col items-center mt-2">
              <LoanOverviewItem
                amount={formData.loan_total_amount}
                title="Total requested loan amount"
                iconHandler={() => setModalState("YEARLY")}
              />
            </div>
            <div className="flex w-full flex-col">
              <div className="mb-4 flex w-full flex-row items-center border-b border-[#ccc] pb-4">
                <DollarCircleIcon />
                <div className="flex w-full items-center px-2.5 text-left text-sm leading-6 font-bold">
                  Total Cost Breakdown
                  {filteredLoanOverviewDetails.length > 0 && (
                    <InfoIconButton
                      marginLeft="8px"
                      onClick={() => {
                        setModalState("COST_BREAKDOWN");
                      }}
                      tabIndex={0}
                    />
                  )}
                </div>
              </div>
              <div className="flex w-full flex-col gap-2">
                {filteredLoanOverviewDetails.map((item, key) => (
                  <div
                    key={key}
                    className="flex w-full flex-row items-center justify-between py-1.25"
                  >
                    <div className="text-left text-sm leading-5 font-normal text-[#687083]">
                      {item.label}
                    </div>
                    <div className="flex items-center justify-between gap-4 text-right text-sm leading-5 font-normal text-[#111827]">
                      {item.type === "dollar"
                        ? `$${toFormattedInteger(item.amount)}`
                        : `${item.amount}%`}
                      <EditIconButton
                        onClick={() => {
                          setEditModalState(item);
                        }}
                        tabIndex={0}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </FormTableBody>
        </FormTable>
        {filteredLoanOverviewDetails.length > 0 && (
          <>
            <MonthlyCalculationModal
              isOpen={modalState === "MONTHLY"}
              loanOverviewFormData={formData}
              closeHandler={closeHandler}
            />
            <CostBreakdownModal
              isOpen={modalState === "COST_BREAKDOWN"}
              closeHandler={closeHandler}
            />
          </>
        )}
        <TotalLoanCalculationModal
          isOpen={modalState === "YEARLY"}
          loanOverviewFormData={formData}
          closeHandler={closeHandler}
        />
        <GenericEditLoanOverviewModal
          editModalState={editModalState}
          loanOverviewFormData={formData}
          closeHandler={closeHandler}
          confirmHandler={confirmHandler}
        />
      </Col>
    );
  };

  return (
    <FormLayout
      title="Loan Overview"
      headerTitle="Down Payment"
      stepIndex={8}
      footerTitle={getFooterTitle()}
      clickHandler={handleFooterClick}
    >
      {renderContent()}
    </FormLayout>
  );
};
